import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { IconTableExport, IconFileExport, IconDatabaseExport, IconFileTypePdf } from '@tabler/icons-react';
import { ChevronDown, Download } from 'lucide-react';
import { exportToCSV, exportToPDF, exportToExcel, ExportOptions, PDFExportOptions, formatExportData } from '@/lib/exportUtils';
import { TableColumn } from '@/modules/app/components/ui/HorizontalTable';
import { toast } from 'sonner';

interface ExportButtonProps {
  data: Record<string, any>[] | any[];
  columns: TableColumn[];
  filename?: string;
  workspaceName?: string;
  tabName?: string;
  className?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  disabled?: boolean;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  columns,
  filename,
  workspaceName,
  tabName,
  className = '',
  variant = 'outline',
  size = 'sm',
  disabled = false,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const getExportOptions = (): ExportOptions => ({
    filename: filename || 'keyword_data',
    includeTimestamp: true,
    customHeaders: columns.reduce((acc, col) => {
      acc[col.key] = col.label;
      return acc;
    }, {} as Record<string, string>)
  });

  const getPDFExportOptions = (): PDFExportOptions => ({
    ...getExportOptions(),
    title: `${filename?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Report` || 'Keyword Data Report',
    subtitle: `Generated from ${workspaceName || 'Keyword Research'}`,
    workspaceName,
    tabName
  });

  const handleExport = async (format: 'csv' | 'pdf' | 'excel') => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    setIsExporting(true);
    
    try {
      const exportOptions = getExportOptions();
      const pdfOptions = getPDFExportOptions();
      const formattedData = formatExportData(data);
      
      switch (format) {
        case 'csv':
          exportToCSV(formattedData, columns, exportOptions);
          toast.success(`CSV exported successfully (${data.length} rows)`);
          break;
        case 'pdf':
          await exportToPDF(formattedData, columns, pdfOptions);
          toast.success(`PDF exported successfully (${data.length} rows)`);
          break;
        case 'excel':
          await exportToExcel(formattedData, columns, exportOptions);
          toast.success(`Excel file exported successfully (${data.length} rows)`);
          break;
        default:
          throw new Error('Unsupported export format');
      }
    } catch (error: unknown) {
      console.error('Export failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Export failed: ${errorMessage}`);
    } finally {
      setIsExporting(false);
    }
  };

  const exportCount = data?.length || 0;
  const hasData = exportCount > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={`flex items-center gap-2 ${className}`}
          disabled={disabled || !hasData || isExporting}
        >
          {isExporting ? (
            <Download className="h-4 w-4 animate-pulse" />
          ) : (
            <IconTableExport className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">
            {isExporting ? 'Exporting...' : 'Export'}
          </span>
          {hasData && (
            <span className="hidden md:inline text-muted-foreground">
              ({exportCount})
            </span>
          )}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem
          onClick={() => handleExport('csv')}
          disabled={!hasData || isExporting}
          className="flex items-center gap-2"
        >
          <IconFileExport className="h-4 w-4" />
          <span>Export as CSV</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem
          onClick={() => handleExport('excel')}
          disabled={!hasData || isExporting}
          className="flex items-center gap-2"
        >
          <IconDatabaseExport className="h-4 w-4" />
          <span>Export as Excel</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem
          onClick={() => handleExport('pdf')}
          disabled={!hasData || isExporting}
          className="flex items-center gap-2"
        >
          <IconFileTypePdf className="h-4 w-4" />
          <span>Export as PDF</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportButton;